import { onAccept } from './handlers/onAccept';
import { onReject } from './handlers/onReject';
import { markAsRead, markAsUnread } from './notificationActions';
import { getNotificationStrategy, normalizeNotificationType } from './strategyRegistry';
import {
  NOTIFICATION_ACTIONS,
  NotificationAction,
  NotificationWorkflowItem,
  NotificationWorkflowResult,
} from './types';

const STATUS_BY_ACTION = {
  [NOTIFICATION_ACTIONS.READ]: 'READ',
  [NOTIFICATION_ACTIONS.UNREAD]: 'UNREAD',
  [NOTIFICATION_ACTIONS.ACCEPT]: 'ACCEPTED',
  [NOTIFICATION_ACTIONS.REJECT]: 'REJECTED',
} as const;

async function onRead(item: NotificationWorkflowItem): Promise<NotificationWorkflowResult> {
  try {
    await markAsRead(item.userNotificationId, item.status);
    return {
      success: true,
      status: 'READ',
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to mark notification as read',
    };
  }
}

async function onUnread(item: NotificationWorkflowItem): Promise<NotificationWorkflowResult> {
  try {
    await markAsUnread(item.userNotificationId, item.status);
    return {
      success: true,
      status: 'UNREAD',
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to mark notification as unread',
    };
  }
}

export async function resolveNotificationAction({
  item,
  action,
}: {
  item: NotificationWorkflowItem;
  action: NotificationAction;
}): Promise<NotificationWorkflowResult> {
  if (action === NOTIFICATION_ACTIONS.READ) {
    return onRead(item);
  }

  if (action === NOTIFICATION_ACTIONS.UNREAD) {
    return onUnread(item);
  }

  const type = normalizeNotificationType(item.type);
  const nextStatus = STATUS_BY_ACTION[action];
  const strategy = getNotificationStrategy(type);

  if (!type || !strategy) {
    console.error('Unsupported notification type', {
      rawType: item.type,
      normalizedType: type,
      action,
      notificationId: item.notificationId,
      userNotificationId: item.userNotificationId,
    });
    return {
      success: false,
      error: `Notification type does not exist: ${item.type ?? 'UNKNOWN'}`,
    };
  }

  try {
    const context = {
      item,
      action,
      nextStatus,
    };

    if (action === NOTIFICATION_ACTIONS.ACCEPT) {
      await onAccept(strategy, context);
    } else {
      await onReject(strategy, context);
    }

    return {
      success: true,
      status: nextStatus,
    };
  } catch (error) {
    console.error('[notification-router] strategy failure', {
      action,
      type,
      notificationId: item.notificationId,
      userNotificationId: item.userNotificationId,
      error,
    });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Notification action failed',
    };
  }
}
